import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';

interface StoryPermission {
  id: number;
  title: string;
  publicationName: string;
  storyOrigin: string;
  userAccessLevel: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

interface AccessLevel {
  value: string;
  label: string;
}

interface UserBasic {
  id: string;
  fullName: string;
  email: string;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px 0;
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FilterLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  min-width: 180px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  ${({ $primary }) =>
    $primary
      ? 'background: #00a6c2; color: white; &:hover { background: #0094ab; }'
      : 'background: #e5e7eb; color: #374151; &:hover { background: #d1d5db; }'}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
`;

const StoryLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const Select = styled.select`
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  background: white;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${({ $active }) => ($active ? '#00a6c2' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#00a6c2' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#374151')};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: ${({ $active }) => ($active ? '#0094ab' : '#f3f4f6')}; }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: #6b7280;
`;

function AdminUserContentOriginAccessPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserBasic | null>(null);
  const [stories, setStories] = useState<StoryPermission[]>([]);
const [pagination, setPagination] = useState<PaginationInfo>({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchFilters, setSearchFilters] = useState({
    storyId: '',
    title: ''
  });

  const fetchData = useCallback(async (page = 1, filters?: typeof searchFilters) => {
    try {
      const params = filters || searchFilters;
      const queryString = new URLSearchParams();
      queryString.append('page', page.toString());
      if (params.storyId) queryString.append('story_id', params.storyId);
      if (params.title) queryString.append('title', params.title);

      const [userRes, permissionsRes] = await Promise.all([
        api.get(`/admin2/users/${id}.json`),
        api.get(`/admin2/users/${id}/content_origin_permissions.json?${queryString.toString()}`)
      ]);
      setUser({
        id: userRes.data.id,
        fullName: `${userRes.data.firstName || ''} ${userRes.data.lastName || ''}`.trim() || userRes.data.email,
        email: userRes.data.email
      });
      setStories(permissionsRes.data.stories || []);
      setPagination(permissionsRes.data.pagination || { currentPage: 1, totalPages: 1, totalCount: 0 });
      setAccessLevels(permissionsRes.data.accessLevels || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [id, searchFilters]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    setLoading(true);
    fetchData(1, searchFilters);
  };

  const handlePageChange = (page: number) => {
    setLoading(true);
    fetchData(page);
  };

  const handleAccessLevelChange = async (storyId: number, newLevel: string) => {
    try {
      await api.put(`/admin2/users/${id}/content_origin_permissions/${storyId}.json`, {
        content_origin: newLevel
      });
      // Update local state
      setStories(stories.map(s => 
        s.id === storyId ? { ...s, userAccessLevel: newLevel } : s
      ));
    } catch (err) {
      console.error('Failed to update access level', err);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const { currentPage, totalPages } = pagination;
    
    // Always show first page
    pages.push(1);
    
    // Show pages around current
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    
    // Show last few pages
    for (let i = Math.max(totalPages - 1, 2); i <= totalPages; i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    
    // Add ellipsis markers
    const result: (number | string)[] = [];
    let prev = 0;
    for (const page of pages.sort((a, b) => a - b)) {
      if (page - prev > 1) result.push('...');
      result.push(page);
      prev = page;
    }
    
    return result;
  };

  if (loading && !user) {
    return (
      <AdminLayout title="Users" pageTitle="Content Origin Access">
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Users" pageTitle={`${user?.fullName || 'User'} - Content Origin Access`}>
      <Breadcrumb>
        <Link to="/admin2/users">Users</Link>
        <span>›</span>
        <Link to={`/admin2/users/${id}/edit`}>{user?.fullName} ({user?.email})</Link>
        <span>›</span>
        <span>Content origin permissions</span>
      </Breadcrumb>

      <PageTitle>{user?.fullName}</PageTitle>

      <TabNav>
        <TabLink to={`/admin2/users/${id}/edit`}>User info</TabLink>
        <TabLink to={`/admin2/users/${id}/publications`}>Publications</TabLink>
        <TabLink to={`/admin2/users/${id}/stories`}>Stories</TabLink>
        <TabLink to={`/admin2/users/${id}/audit_events`}>Audit Events</TabLink>
        <TabLink to={`/admin2/users/${id}/content_origin_access`} $active>Content Origin Access</TabLink>
      </TabNav>

      <Section>
        <SectionTitle>Content Origin Permissions</SectionTitle>
        
        <SearchRow>
          <FilterGroup>
            <FilterLabel>Story ID</FilterLabel>
            <Input
              type="text"
              placeholder="Enter story ID"
              value={searchFilters.storyId}
              onChange={(e) => setSearchFilters({ ...searchFilters, storyId: e.target.value })}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Story Title</FilterLabel>
            <Input
              type="text"
              placeholder="Enter story title"
              value={searchFilters.title}
              onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
            />
          </FilterGroup>

          <Button $primary onClick={handleSearch}>Search</Button>
        </SearchRow>

        {stories.length === 0 ? (
          <EmptyState>No stories found.</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Story Title</Th>
                  <Th>Publication</Th>
                  <Th>Story Origin</Th>
                  <Th>User Access Level</Th>
                </tr>
              </thead>
              <tbody>
                {stories.map((story) => (
                  <tr key={story.id}>
                    <Td>
                      <StoryLink to={`/stories/${story.id}`}>{story.title}</StoryLink>
                    </Td>
                    <Td>{story.publicationName}</Td>
                    <Td>{story.storyOrigin}</Td>
                    <Td>
                      <Select
                        value={story.userAccessLevel}
                        onChange={(e) => handleAccessLevelChange(story.id, e.target.value)}
                      >
                        {accessLevels.map((level) => (
                          <option key={level.value} value={level.value}>{level.label}</option>
                        ))}
                      </Select>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {pagination.totalPages > 1 && (
              <Pagination>
                {renderPagination().map((item, idx) => (
                  typeof item === 'number' ? (
                    <PageButton
                      key={idx}
                      $active={item === pagination.currentPage}
                      onClick={() => handlePageChange(item)}
                    >
                      {item}
                    </PageButton>
                  ) : (
                    <span key={idx}>...</span>
                  )
                ))}
                <PageButton
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                >
                  →
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                >
                  ⇥
                </PageButton>
              </Pagination>
            )}
          </>
        )}
      </Section>
    </AdminLayout>
  );
}

export default AdminUserContentOriginAccessPage;
